/**
 * Fits the backdrop picture into the screen (contain: the whole picture is always visible) and
 * converts between screen and picture coordinates. Pure, so it is unit-tested.
 */
export interface Fit {
	/** Screen pixels per picture pixel. */
	scale: number;
	/** The picture's top-left corner on screen. */
	x: number;
	y: number;
}

/** Screen space kept free around the picture, as a share of the smaller screen side. */
export const FIT_MARGIN = 0.02;
/**
 * A tap counts this many screen pixels beyond a spot's edge: small fingers on small screens
 * still hit the small spots (the closest lit light wins when several are in reach).
 */
export const TAP_SLOP_PX = 22;

export function fitContain(
	screenW: number,
	screenH: number,
	imageW: number,
	imageH: number,
	margin = FIT_MARGIN,
): Fit {
	const pad = Math.min(screenW, screenH) * margin;
	const scale = Math.max(
		0.01,
		Math.min((screenW - 2 * pad) / imageW, (screenH - 2 * pad) / imageH),
	);
	return { scale, x: (screenW - imageW * scale) / 2, y: (screenH - imageH * scale) / 2 };
}

export function screenToImage(fit: Fit, sx: number, sy: number): { x: number; y: number } {
	return { x: (sx - fit.x) / fit.scale, y: (sy - fit.y) / fit.scale };
}

export function imageToScreen(fit: Fit, ix: number, iy: number): { x: number; y: number } {
	return { x: fit.x + ix * fit.scale, y: fit.y + iy * fit.scale };
}

/** The tap reach in picture pixels for this fit; `pixelRatio` = canvas pixels per CSS pixel. */
export function tapReach(fit: Fit, pixelRatio = 1): number {
	return (TAP_SLOP_PX * pixelRatio) / fit.scale;
}
